import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Pencil, ExternalLink, RefreshCw, Loader2 } from "lucide-react";

const MYRLC_URL = "https://www.rlcarriers.com/company/myrlc";

// Modify an R+L BOL WITHOUT changing the pro: R+L has no "edit BOL" API, so the shipper edits the BOL
// in the MyRLC portal (keeps the pro), then Commander re-pulls R+L's official record + document.
export default function ResyncBolDialog({ open, onOpenChange, quoteId, quoteNumber, proNumber, onSynced }) {
  const [busy, setBusy] = useState(false);

  const resync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/resync-bol`);
      const bits = [
        data.handling_units != null && `${data.handling_units} handling units`,
        data.total_weight != null && `${Math.round(data.total_weight)} lb`,
        data.calculated_class && `class ${data.calculated_class}`,
      ].filter(Boolean).join(" · ");
      toast.success(`Re-pulled R+L BOL ${data.pro_number}${bits ? ` — ${bits}` : ""}.`);
      onOpenChange(false);
      onSynced?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="resync-bol-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Pencil className="h-5 w-5 text-[hsl(var(--primary))]" /> Modify BOL — {quoteNumber}
          </DialogTitle>
          <DialogDescription>
            R+L doesn&apos;t allow editing a BOL through the API, so the pro number can never change. To correct
            this shipment while keeping the same pro <span className="font-semibold text-foreground">{proNumber}</span>,
            edit it in the R+L portal, then re-pull the official BOL here.
          </DialogDescription>
        </DialogHeader>

        <ol className="space-y-3 py-1 text-sm">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/15 text-xs font-bold text-[hsl(var(--primary))]">1</span>
            <div>
              <div className="font-medium">Open the BOL in the MyRLC portal and make your edits</div>
              <div className="text-muted-foreground">Sign in, find pro <span className="font-semibold">{proNumber}</span> under your Bills of Lading, and correct it. The pro number stays the same.</div>
              <Button variant="outline" size="sm" asChild className="mt-2 gap-1.5">
                <a href={MYRLC_URL} target="_blank" rel="noopener noreferrer" data-testid="resync-open-portal">
                  <ExternalLink className="h-3.5 w-3.5" /> Open MyRLC portal
                </a>
              </Button>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/15 text-xs font-bold text-[hsl(var(--primary))]">2</span>
            <div>
              <div className="font-medium">Re-pull the updated BOL into Commander</div>
              <div className="text-muted-foreground">Commander fetches R+L&apos;s official document and syncs the shipment details (handling units, weight, class, addresses, accessorials) — no new pro, nothing generated locally.</div>
            </div>
          </li>
        </ol>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={resync} disabled={busy} data-testid="resync-bol-btn" className="gap-2 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Re-pull R+L BOL
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
